import { ErrorTypes } from '@fabric-ui/components';
import { envVariables } from '@fabric-ui/utils';
import { isDebugMode, isSynthetics, type RumUser } from '@utils/AvRum';
import { isUsableId, normalizeId } from '@utils/identity.utils';

type UserpilotSdk = {
  identify: (userId: string, properties?: Record<string, unknown>) => void;
  reload: () => void;
  track: (name: string, properties?: Record<string, unknown>) => void;
  clean: () => void;
};

declare const window: {
  userpilot?: UserpilotSdk;
  userpilotSettings?: { token: string };
} & Window;

const REQUIRED_IDS = ['userId', 'accountId'] as const;
const SCRIPT_LOAD_TIMEOUT_MS = 30_000;

// Userpilot is controlled by its own token only. Whether Sentry or Pendo is configured has no effect on it.
const isUserpilotEnabled = () => !isDebugMode() && !isSynthetics && !envVariables.STORYBOOK && !!envVariables.VITE_USERPILOT_TOKEN;

const state = {
  sdk: undefined as UserpilotSdk | undefined,
  failed: false,
  // Userpilot needs identify before reload or track, and an unidentified user would count as an anonymous active user.
  identified: false,
  pending: [] as Array<(sdk: UserpilotSdk) => void>,
};

// The SDK loads asynchronously. Only identify and clean change Userpilot's state, so only they wait for it, and the latest
// one replaces any earlier one, so the queue never holds more than one call even if the script is blocked.
// reload and track are dropped before the SDK has loaded: the waiting identify reloads the current page itself, and a user
// who is not identified yet must not be tracked.
const enabledSdk = () => (isUserpilotEnabled() && !state.failed ? state.sdk : undefined);

const whenLoaded = (call: (sdk: UserpilotSdk) => void) => {
  if (!isUserpilotEnabled() || state.failed) return;
  if (state.sdk) call(state.sdk);
  else state.pending = [call];
};

export const initializeUserpilotScript = () => {
  if (!isUserpilotEnabled()) return;
  window.userpilotSettings = { token: envVariables.VITE_USERPILOT_TOKEN };
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://js.userpilot.io/sdk/latest.js';
  // A script that is reachable but hangs fires neither onload nor onerror, so report it instead of waiting silently.
  const timeout = setTimeout(() => {
    state.failed = true;
    state.pending = [];
    console.error(`${ErrorTypes.Userpilot}: Userpilot script did not load within ${SCRIPT_LOAD_TIMEOUT_MS / 1000} seconds.`);
  }, SCRIPT_LOAD_TIMEOUT_MS);
  script.onload = () => {
    clearTimeout(timeout);
    state.failed = false;
    const sdk = window.userpilot;
    if (!sdk) {
      state.failed = true;
      state.pending = [];
      console.error(`${ErrorTypes.Userpilot}: Userpilot script loaded but window.userpilot is not defined.`);
      return;
    }
    state.sdk = sdk;
    state.pending.splice(0).forEach(call => call(sdk));
  };
  script.onerror = (...args) => {
    clearTimeout(timeout);
    state.failed = true;
    state.pending = [];
    console.error(`${ErrorTypes.Userpilot}: Userpilot script failed to load.`, args);
  };
  document.head.appendChild(script);
};

const withoutEmpty = (record: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined && value !== null && value !== ''));

export const identifyUserpilot = (userData?: RumUser) => {
  if (!isUserpilotEnabled()) return;
  const missingIds = REQUIRED_IDS.filter(key => !isUsableId(userData?.[key]));
  if (missingIds.length || !userData) {
    console.error(
      `${ErrorTypes.Userpilot}: Userpilot identify skipped, missing required ${(missingIds.length ? missingIds : REQUIRED_IDS).join(' and ')}.`
    );
    // The previous user, or one still waiting for the SDK, must not stay attached to later reloads and events.
    if (state.identified || state.pending.length) clearUserpilotSession();
    return;
  }
  const { email, roleId, roleName, role, accountName, accountType, accountCreated } = userData;
  const env = envVariables.VITE_ENV;
  whenLoaded(sdk => {
    sdk.identify(`${normalizeId(userData.userId)}_${env}`, {
      ...withoutEmpty({
        email: typeof email === 'string' ? email.replace(/^[^@]+/, '****') : '',
        role,
        role_id: roleId,
        role_name: roleName,
      }),
      company: withoutEmpty({
        id: `${normalizeId(userData.accountId)}_${env}`,
        name: accountName,
        type: accountType,
        created_at: accountCreated,
      }),
    });
    state.identified = true;
    sdk.reload();
  });
};

// Single page app: Userpilot has to be told about every route change, and only after identify.
export const reloadUserpilot = () => {
  const sdk = enabledSdk();
  if (sdk && state.identified) sdk.reload();
};

export const trackUserpilotEvent = (name: string, properties?: Record<string, unknown>) => {
  const sdk = enabledSdk();
  if (sdk && state.identified) sdk.track(name, properties);
};

// clean() rather than anonymous(): an anonymous user counts towards the monthly active users.
export const clearUserpilotSession = () =>
  whenLoaded(sdk => {
    sdk.clean();
    state.identified = false;
  });
